import { useState } from 'react';

interface ListingCardProps {
  listing: any;
  onClick?: () => void;
  onDetailClick?: () => void;
  explanation?: string;
  onRequestExplanation?: () => void;
  isLoadingExplanation?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  isFavorite?: boolean;
  onToggleFavorite?: (listingId: number) => void;
  isCompare?: boolean;
  onToggleCompare?: (listingId: number) => void;
  compareDisabled?: boolean;
}

export default function ListingCard({ 
  listing, 
  onClick, 
  onDetailClick,
  explanation, 
  onRequestExplanation,
  isLoadingExplanation,
  onMouseEnter,
  onMouseLeave,
  isFavorite,
  onToggleFavorite,
  isCompare,
  onToggleCompare,
  compareDisabled,
}: ListingCardProps) {
  const score = listing.score ?? 0;
  const status = listing.status ?? 'pass';
  const [animating, setAnimating] = useState(false);
  const [compareAnimating, setCompareAnimating] = useState(false);

  // Format price
  const price = listing.price_chf
    ? `CHF ${Number(listing.price_chf).toLocaleString('de-CH')}`
    : listing.rent_gross_chf
    ? `CHF ${Number(listing.rent_gross_chf).toLocaleString('de-CH')}/Mt.`
    : 'Preis auf Anfrage';

  function handleFavoriteClick(e: React.MouseEvent) {
    e.stopPropagation();
    if (onToggleFavorite) {
      setAnimating(true);
      onToggleFavorite(listing.id);
      setTimeout(() => setAnimating(false), 300);
    }
  }

  function handleCompareClick(e: React.MouseEvent) {
    e.stopPropagation();
    if (onToggleCompare && (!compareDisabled || isCompare)) {
      setCompareAnimating(true);
      onToggleCompare(listing.id);
      setTimeout(() => setCompareAnimating(false), 300);
    }
  }

  const thumbUrl = listing.thumbnail_url || null;

  return (
    <div 
      className={`listing-card listing-card--${status}`} 
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="listing-card-body">
        {/* Score + Thumbnail stacked vertically */}
        <div className="listing-card-left">
          <div className={`score-badge score-badge--${status}`}>
            {score}
          </div>
          <div className="listing-card-thumb-wrap">
            {thumbUrl ? (
              <img src={thumbUrl} alt="" className="listing-card-thumb" loading="lazy" />
            ) : (
              <div className="listing-card-thumb listing-card-thumb--placeholder">
                <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="6" width="18" height="13" rx="2"/>
                  <circle cx="9" cy="12" r="2"/>
                  <path d="M21 19l-5-5-3 3-2-2-5 5"/>
                </svg>
              </div>
            )}
          </div>
        </div>

        {/* Right content */}
        <div className="listing-card-content">
          <div className="listing-card-header">
            <div className="listing-card-info">
              <h3 className="listing-title">{listing.title || 'Immobilie'}</h3>
              <p className="listing-location">
                {[listing.zip_code, listing.city, listing.canton].filter(Boolean).join(', ')}
                {' – '}
                <span className="listing-address-inline">
                  {listing.street 
                    ? `${listing.street}${listing.street_number ? ' ' + listing.street_number : ''}` 
                    : listing.address 
                      ? listing.address 
                      : 'Genaue Adresse nicht vorhanden'}
                </span>
              </p>
            </div>
            <div className="listing-card-actions">
              {onToggleCompare && (
            <button
              className={`compare-btn ${isCompare ? 'compare-btn--active' : ''} ${compareAnimating ? 'compare-btn--animating' : ''} ${compareDisabled && !isCompare ? 'compare-btn--disabled' : ''}`}
              onClick={handleCompareClick}
              title={isCompare ? 'Aus Vergleich entfernen' : compareDisabled ? 'Max. 3 Objekte zum Vergleich' : 'Zum Vergleich hinzufügen'}
              aria-label={isCompare ? 'Aus Vergleich entfernen' : 'Zum Vergleich hinzufügen'}
              disabled={compareDisabled && !isCompare}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="20" x2="18" y2="10" />
                <line x1="12" y1="20" x2="12" y2="4" />
                <line x1="6" y1="20" x2="6" y2="14" />
                <line x1="3" y1="20" x2="21" y2="20" />
              </svg>
            </button>
          )}
          {onToggleFavorite && (
            <button
              className={`favorite-btn ${isFavorite ? 'favorite-btn--active' : ''} ${animating ? 'favorite-btn--animating' : ''}`}
              onClick={handleFavoriteClick}
              title={isFavorite ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen'}
              aria-label={isFavorite ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen'}
            >
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
                  fill={isFavorite ? 'currentColor' : 'none'}
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
              </svg>
            </button>
          )}
            </div>
          </div>

          <div className="listing-card-details">
            <span className="listing-detail">{price}</span>
            {listing.rooms && <span className="listing-detail">{listing.rooms} Zi.</span>}
            {listing.living_space_m2 && <span className="listing-detail">{listing.living_space_m2} m²</span>}
            {listing.property_type && listing.property_type !== 'other' && (
              <span className="listing-detail listing-type">{listing.property_type}</span>
            )}
          </div>

          {/* Tags */}
          <div className="listing-tags">
            {listing.fulfilled?.map((f: string, i: number) => (
              <span key={i} className="tag tag--pass">{f}</span>
            ))}
            {listing.nearMissReasons?.map((r: any, i: number) => (
              <span key={`n${i}`} className="tag tag--near">{r.message}</span>
            ))}
          </div>
        </div>{/* end listing-card-content */}
      </div>{/* end listing-card-body */}

      {/* AI Explanation */}
      {explanation && (
        <div className="listing-explanation">
          <div className="listing-explanation-icon">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7v1a2 2 0 0 1-2 2h-1v1a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2v-1H5a2 2 0 0 1-2-2v-1a7 7 0 0 1 7-7h1V5.73A2 2 0 0 1 12 2z"/>
              <circle cx="9" cy="13" r="1.25" fill="currentColor" stroke="none"/>
              <circle cx="15" cy="13" r="1.25" fill="currentColor" stroke="none"/>
              <path d="M10 17h4"/>
            </svg>
          </div>
          <p className="listing-explanation-text">{explanation}</p>
        </div>
      )}
      
      {!explanation && onRequestExplanation && (
        <button
          className="listing-explain-btn"
          onClick={(e) => {
            e.stopPropagation();
            onRequestExplanation();
          }}
          disabled={isLoadingExplanation}
        >
          {isLoadingExplanation ? 'Generiere Erklärung...' : 'Warum passt diese Immobilie?'}
        </button>
      )}

      {onDetailClick && (
        <button
          className="listing-detail-btn"
          onClick={(e) => {
            e.stopPropagation();
            onDetailClick();
          }}
        >
          Detailbeschreibung
        </button>
      )}
    </div>
  );
}
